/**
 * School Student Roster - Tab 3 of School Admin Dashboard
 *
 * Displays complete student roster with:
 * - Student information from Schoology (via lms_students)
 * - Engagement metrics (sessions, hours, last active)
 * - Grade level filtering
 * - Engagement level filtering
 * - Search by name/email
 * - Pagination
 *
 * Data Sources:
 * - GET /api/admin/school/students (session-based - no ID)
 * - Clever SSO (User table)
 * - Schoology LMS (LMSStudent table)
 * - Learning analytics (ClassroomSession table)
 */

import React, { useState, useEffect } from 'react';
import axios from '../utils/authInterceptor';

// Tappy Brand Colors - Official 2025 Design System
const TappyBrandColors = {
  primary: {
    darkTeal: '#1A3E48',
    teal: '#297A92',
    mediumTeal: '#388899',
    brandBlue: '#76909F',
    brightTeal: '#42909E',
    aqua: '#4D99A5',
    lightAqua: '#5BA3AC',
    skyBlue: '#6DB4CD',
  },
  accent: {
    coral: '#EA6D37',
    gold: '#ECC962',
    brightGold: '#F8C341',
    tan: '#C59F65',
  },
  neutral: {
    darkGray: '#3D4954',
    mediumGray: '#8D7F88',
    lightTeal: '#ABCACA',
    paleGray: '#D5DEDD',
    lightGray: '#ECF3F2',
    veryLight: '#F5F9FA',
    almostWhite: '#FCFDFE',
  },
  semantic: {
    success: '#42909E',
    warning: '#ECC962',
    error: '#EA6D37',
    info: '#6DB4CD',
  }
};

function SchoolStudentRoster({ getApiUrl, isDistrictAdminView }) {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    grade: '',
    engagement: '',
    search: '',
    page: 1,
    per_page: 50
  });

  // Helper to get API URL - use prop if provided, otherwise default to session-based
  const buildApiUrl = (endpoint) => {
    if (getApiUrl) {
      return getApiUrl(endpoint);
    }
    return `/api/admin/school/${endpoint}`;
  };

  // Fetch students when filters change
  useEffect(() => {
    fetchStudents();
  }, [filters]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.grade) params.append('grade', filters.grade);
      if (filters.engagement) params.append('engagement', filters.engagement);
      if (filters.search) params.append('search', filters.search);
      params.append('page', filters.page);
      params.append('per_page', filters.per_page);

      const response = await axios.get(`${buildApiUrl('students')}?${params}`);
      setStudents(response.data.students || []);
      setTotal(response.data.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching students:', err);
      setError(err.response?.data?.error || 'Failed to load student roster');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : 1 // Reset to page 1 when changing filters
    }));
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    if (seconds < 2592000) return `${Math.floor(seconds / 604800)}w ago`;
    return date.toLocaleDateString();
  };

  const getEngagementColor = (level) => {
    switch (level) {
      case 'high': return { bg: '#d1fae5', text: '#065f46', label: 'High' };
      case 'medium': return { bg: '#fef3c7', text: '#92400e', label: 'Medium' };
      case 'low': return { bg: '#fee2e2', text: '#991b1b', label: 'Low' };
      default: return { bg: '#f3f4f6', text: '#6b7280', label: 'Unknown' };
    }
  };

  const getEnergyColor = (priority) => {
    switch (priority) {
      case 'high': return { bg: TappyBrandColors.semantic.error + '15', text: TappyBrandColors.semantic.error, label: 'High', icon: '!' };
      case 'medium': return { bg: TappyBrandColors.semantic.warning + '15', text: TappyBrandColors.semantic.warning, label: 'Medium', icon: '~' };
      case 'on_track': return { bg: TappyBrandColors.semantic.success + '15', text: TappyBrandColors.semantic.success, label: 'On Track', icon: '✓' };
      default: return { bg: TappyBrandColors.neutral.lightGray, text: TappyBrandColors.neutral.mediumGray, label: 'N/A', icon: '-' };
    }
  };

  const totalPages = Math.ceil(total / filters.per_page);

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          margin: '0 auto 20px',
          borderRadius: '50%',
          backgroundColor: TappyBrandColors.accent.coral + '15',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          color: TappyBrandColors.accent.coral
        }}>!</div>
        <h3 style={{ color: TappyBrandColors.accent.coral, marginBottom: '12px', fontWeight: 600 }}>Error Loading Students</h3>
        <p style={{ color: TappyBrandColors.neutral.mediumGray, marginBottom: '20px' }}>{error}</p>
        <button
          onClick={fetchStudents}
          style={{
            padding: '10px 20px',
            backgroundColor: '#4F46E5',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
            Student Roster
          </h2>
          <p style={{ fontSize: '14px', color: '#6b7280' }}>
            {total} total students • Showing {students.length} on page {filters.page}
          </p>
        </div>
        <button
          onClick={fetchStudents}
          style={{
            padding: '10px 20px',
            backgroundColor: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#374151'
          }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Filters */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          {/* Search */}
          <input
            type="text"
            placeholder="Search by name or email..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            style={{
              padding: '10px 14px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none'
            }}
            onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />

          {/* Grade Filter */}
          <select
            value={filters.grade}
            onChange={(e) => handleFilterChange('grade', e.target.value)}
            style={{
              padding: '10px 14px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">All Grades</option>
            <option value="6th">6th Grade</option>
            <option value="7th">7th Grade</option>
            <option value="8th">8th Grade</option>
            <option value="9th">9th Grade</option>
            <option value="10th">10th Grade</option>
            <option value="11th">11th Grade</option>
            <option value="12th">12th Grade</option>
          </select>

          {/* Engagement Filter */}
          <select
            value={filters.engagement}
            onChange={(e) => handleFilterChange('engagement', e.target.value)}
            style={{
              padding: '10px 14px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">All Engagement Levels</option>
            <option value="high">High (80%+)</option>
            <option value="medium">Medium (50-79%)</option>
            <option value="low">Low (&lt;50%)</option>
          </select>

          {/* Per Page */}
          <select
            value={filters.per_page}
            onChange={(e) => handleFilterChange('per_page', parseInt(e.target.value))}
            style={{
              padding: '10px 14px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
            <option value="200">200 per page</option>
          </select>
        </div>
      </div>

      {/* Student Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
            <p style={{ color: '#6b7280' }}>Loading students...</p>
          </div>
        ) : students.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 20px',
              borderRadius: '12px',
              backgroundColor: TappyBrandColors.neutral.lightGray,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              color: TappyBrandColors.neutral.mediumGray
            }}>□</div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: TappyBrandColors.primary.darkTeal, marginBottom: '8px' }}>
              No Students Found
            </h3>
            <p style={{ fontSize: '14px', color: TappyBrandColors.neutral.mediumGray }}>
              {filters.search || filters.grade || filters.engagement
                ? 'Try adjusting your filters'
                : 'No students enrolled yet'}
            </p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'left',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Student Name
                    </th>
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'left',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Grade
                    </th>
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'left',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Email
                    </th>
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'center',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Sessions
                    </th>
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'center',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Hours
                    </th>
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'left',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Last Active
                    </th>
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'center',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Energy
                    </th>
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'center',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Engagement
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, index) => {
                    const engagement = getEngagementColor(student.engagement_level);
                    return (
                      <tr
                        key={student.id}
                        style={{
                          borderBottom: '1px solid #f3f4f6',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ padding: '16px 20px' }}>
                          <div style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937' }}>
                            {student.first_name} {student.last_name}
                          </div>
                          {student.username && (
                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                              @{student.username}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '16px 20px' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            backgroundColor: '#f3f4f6',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: '#374151'
                          }}>
                            {student.grade_level || 'N/A'}
                          </span>
                        </td>
                        <td style={{ padding: '16px 20px', fontSize: '14px', color: '#6b7280' }}>
                          {student.email || '—'}
                        </td>
                        <td style={{ padding: '16px 20px', textAlign: 'center', fontSize: '14px', fontWeight: 500, color: '#1f2937' }}>
                          {student.total_sessions || 0}
                        </td>
                        <td style={{ padding: '16px 20px', textAlign: 'center', fontSize: '14px', fontWeight: 500, color: '#10b981' }}>
                          {student.total_hours ? student.total_hours.toFixed(1) : '0.0'}
                        </td>
                        <td style={{ padding: '16px 20px', fontSize: '13px', color: '#6b7280' }}>
                          {formatTimeAgo(student.last_session || student.last_login)}
                        </td>
                        <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                          {student.energy_score !== null && student.energy_score !== undefined ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '6px 14px',
                                backgroundColor: getEnergyColor(student.energy_priority).bg,
                                color: getEnergyColor(student.energy_priority).text,
                                borderRadius: '16px',
                                fontSize: '12px',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                              }}>
                                {getEnergyColor(student.energy_priority).label}
                              </span>
                              <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                {student.energy_score}
                              </span>
                            </div>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#9ca3af' }}>No data</span>
                          )}
                        </td>
                        <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '6px 14px',
                            backgroundColor: engagement.bg,
                            color: engagement.text,
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>
                            {engagement.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                padding: '20px',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  Showing {((filters.page - 1) * filters.per_page) + 1} to {Math.min(filters.page * filters.per_page, total)} of {total} students
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleFilterChange('page', filters.page - 1)}
                    disabled={filters.page === 1}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: filters.page === 1 ? '#f3f4f6' : 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: filters.page === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      color: filters.page === 1 ? '#9ca3af' : '#374151'
                    }}
                  >
                    ← Previous
                  </button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 12px' }}>
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>
                      Page {filters.page} of {totalPages}
                    </span>
                  </div>
                  <button
                    onClick={() => handleFilterChange('page', filters.page + 1)}
                    disabled={filters.page === totalPages}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: filters.page === totalPages ? '#f3f4f6' : 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: filters.page === totalPages ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      color: filters.page === totalPages ? '#9ca3af' : '#374151'
                    }}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Export Actions - Tappy Brand Style */}
      <div style={{
        marginTop: '24px',
        padding: '20px',
        backgroundColor: TappyBrandColors.neutral.paleGray,
        border: `2px solid ${TappyBrandColors.primary.teal}`,
        borderLeft: `4px solid ${TappyBrandColors.primary.teal}`,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        boxShadow: `0 4px 12px rgba(41, 122, 146, 0.15)`
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          backgroundColor: 'white',
          border: `2px solid ${TappyBrandColors.primary.teal}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <div style={{
            fontSize: '20px',
            fontWeight: 600,
            color: TappyBrandColors.primary.teal
          }}>📥</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontWeight: 600,
            color: TappyBrandColors.neutral.darkGray,
            marginBottom: '6px',
            fontSize: '15px'
          }}>
            Export Student Roster
          </div>
          <div style={{
            fontSize: '13px',
            color: TappyBrandColors.primary.brandBlue,
            lineHeight: '1.5'
          }}>
            Download comprehensive student data for reporting and analysis
          </div>
        </div>
        <button
          onClick={async () => {
            try {
              const response = await axios.post('/api/admin/school/export/students', {}, {
                responseType: 'blob'
              });
              const url = window.URL.createObjectURL(new Blob([response.data]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', `student_roster_${new Date().toISOString().split('T')[0]}.csv`);
              document.body.appendChild(link);
              link.click();
              link.remove();
            } catch (err) {
              console.error('Export failed:', err);
              alert('Failed to export data');
            }
          }}
          style={{
            padding: '10px 20px',
            backgroundColor: TappyBrandColors.primary.teal,
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
            flexShrink: 0,
            transition: 'all 200ms ease',
            boxShadow: `0 2px 8px rgba(41, 122, 146, 0.25)`
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = TappyBrandColors.primary.brightTeal;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = TappyBrandColors.primary.teal;
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Download CSV
        </button>
      </div>

      {/* Help Text */}
      <div style={{
        marginTop: '20px',
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          <strong>Engagement levels:</strong> High = 8+ sessions/month, Medium = 4-7 sessions/month, Low = less than 4 sessions/month
        </div>
      </div>
    </div>
  );
}

export default SchoolStudentRoster;
